import { Link } from 'react-router-dom'
import SEOHead from '../components/seo/SEOHead'
import { applyCatalogueFacts } from '../content/marketing/catalogueFacts'
import citationContent from '../content/marketing/citation-pages.json'

const SITE = 'https://learn.rondanini.com'

type CoverageLink = { href: string; label: string; detail: string }
type Check = { flag: string; category: string; verify: string; response: string }
type Faq = { question: string; answer: string }
type Section = { heading: string; paragraphs?: string[]; bullets?: string[] }

export type CitationPage = {
  slug: string
  metaTitle: string
  metaDescription: string
  keywords: string[]
  h1: string
  lede: string
  sections: Section[]
  coverage: CoverageLink[]
  checks: Check[]
  faqs: Faq[]
  cta: { href: string; label: string }
  secondaryCta?: { href: string; label: string }
}

const pages = citationContent.pages as CitationPage[]

export function getCitationPage(slug: string): CitationPage | undefined {
  return pages.find((page) => page.slug === slug)
}

function faqSchema(page: CitationPage) {
  return {
    '@type': 'FAQPage',
    '@id': `${SITE}/${page.slug}#faq`,
    mainEntity: page.faqs.map((faq) => ({
      '@type': 'Question',
      name: applyCatalogueFacts(faq.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: applyCatalogueFacts(faq.answer),
      },
    })),
  }
}

function pageSchema(page: CitationPage) {
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebPage',
      '@id': `${SITE}/${page.slug}#webpage`,
      url: `${SITE}/${page.slug}`,
      name: page.h1,
      description: applyCatalogueFacts(page.metaDescription),
      inLanguage: 'en-GB',
      isPartOf: { '@type': 'WebSite', name: 'Professional Treasury Education', url: SITE },
      about: { '@type': 'Thing', name: 'Corporate treasury education' },
      publisher: {
        '@type': 'Organization',
        name: 'Professional Treasury Education',
        legalName: 'Rondanini Publishing Ltd',
        url: SITE,
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: page.metaTitle, item: `${SITE}/${page.slug}` },
      ],
    },
  ]
  if (page.faqs.length > 0) graph.push(faqSchema(page))
  return { '@context': 'https://schema.org', '@graph': graph }
}

export default function CitationLanding({ slug }: { slug: string }) {
  const page = getCitationPage(slug)
  if (!page) return null

  const text = applyCatalogueFacts
  const others = pages.filter((item) => item.slug !== page.slug)

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-navy-900">
      <SEOHead
        title={page.metaTitle}
        description={text(page.metaDescription)}
        canonicalUrl={`${SITE}/${page.slug}`}
        ogType="website"
        keywords={page.keywords}
        jsonLd={pageSchema(page)}
      />

      <section className="bg-gradient-to-br from-navy-800 via-navy-900 to-navy-800 text-white">
        <div className="max-w-4xl mx-auto container-padding py-16">
          <p className="text-gold-400 text-sm font-semibold tracking-wide uppercase mb-4">
            Professional Treasury Education
          </p>
          <h1 className="font-serif text-3xl md:text-5xl font-bold mb-6">{page.h1}</h1>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed">{text(page.lede)}</p>
          <p className="mt-4 text-sm text-gray-400">
            Published by Rondanini Publishing Ltd, England and Wales, company number 16548159.
          </p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto container-padding py-12 space-y-10">
        {page.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="font-serif text-2xl font-bold text-navy-800 dark:text-white mb-4">
              {section.heading}
            </h2>
            <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{text(paragraph)}</p>
              ))}
              {section.bullets && section.bullets.length > 0 && (
                <ul className="list-disc pl-5 space-y-2">
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{text(bullet)}</li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        ))}

        {page.coverage.length > 0 && (
          <ul className="grid sm:grid-cols-2 gap-4">
            {page.coverage.map((item) => (
              <li key={item.href} className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-lg p-4">
                <Link to={item.href} className="font-semibold text-navy-800 dark:text-gold-400 hover:underline">
                  {item.label}
                </Link>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{item.detail}</p>
              </li>
            ))}
          </ul>
        )}

        {page.checks.length > 0 && (
          <section>
            <h2 className="font-serif text-2xl font-bold text-navy-800 dark:text-white mb-4">
              Red flags, and the published answer
            </h2>
            <div className="space-y-4">
              {page.checks.map((check) => (
                <div
                  key={check.flag}
                  className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-lg p-5"
                >
                  <p className="text-xs uppercase tracking-wide text-gold-700 dark:text-gold-400 mb-1">
                    {check.category}
                  </p>
                  <h3 className="text-lg font-semibold text-navy-800 dark:text-white mb-2">{check.flag}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <span className="font-semibold">Check: </span>
                    {check.verify}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{text(check.response)}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {page.faqs.length > 0 && (
          <section id="faq">
            <h2 className="font-serif text-2xl font-bold text-navy-800 dark:text-white mb-4">
              Questions these pages answer
            </h2>
            <div className="space-y-4">
              {page.faqs.map((faq) => (
                <details
                  key={faq.question}
                  open
                  className="bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-700 rounded-lg p-5"
                >
                  <summary className="font-semibold text-navy-800 dark:text-white cursor-pointer">
                    {text(faq.question)}
                  </summary>
                  <p className="mt-3 text-gray-700 dark:text-gray-300 leading-relaxed">{text(faq.answer)}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Link
            to={page.cta.href}
            className="bg-gold-600 text-navy-900 px-6 py-3 rounded-lg font-semibold text-center hover:bg-gold-500"
          >
            {page.cta.label}
          </Link>
          {page.secondaryCta && (
            <Link
              to={page.secondaryCta.href}
              className="border-2 border-navy-800 dark:border-gold-400 text-navy-800 dark:text-gold-400 px-6 py-3 rounded-lg font-semibold text-center hover:bg-navy-800 hover:text-white dark:hover:bg-gold-400 dark:hover:text-navy-900"
            >
              {page.secondaryCta.label}
            </Link>
          )}
        </div>

        <nav aria-label="Related treasury pages" className="pt-6 border-t border-gray-200 dark:border-navy-700">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            Related
          </h2>
          <ul className="space-y-2">
            {others.map((item) => (
              <li key={item.slug}>
                <Link to={`/${item.slug}`} className="text-navy-800 dark:text-gold-400 hover:underline">
                  {item.metaTitle}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </article>
    </div>
  )
}
